import { useEffect } from 'react'
import { getContrastColor } from '../utils/helper'

/**
 * Normalize #RGB / #RRGGBB / rgb() into #RRGGBB for HSL conversion.
 */
function normalizeHex(color) {
  if (!color || typeof color !== 'string') return null

  if (color.startsWith('rgb')) {
    const rgbMatch = color.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i)
    if (!rgbMatch) return null
    return `#${[rgbMatch[1], rgbMatch[2], rgbMatch[3]]
      .map(channel =>
        Math.max(0, Math.min(255, parseInt(channel, 10)))
          .toString(16)
          .padStart(2, '0')
      )
      .join('')}`
  }

  const raw = color.trim().replace('#', '')
  if (/^[a-f\d]{3}$/i.test(raw)) {
    return `#${raw
      .split('')
      .map(char => char + char)
      .join('')}`
  }
  if (/^[a-f\d]{6}$/i.test(raw)) return `#${raw}`
  return null
}

/**
 * Converts a hex color string to HSL components string (e.g. "222.2 47.4% 11.2%")
 * as required by the CSS variable format used in index.css / tailwind.config.js
 */
function hexToHslComponents(hex) {
  const normalized = normalizeHex(hex)
  if (!normalized) return null
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(normalized)
  if (!result) return null
  let r = parseInt(result[1], 16) / 255
  let g = parseInt(result[2], 16) / 255
  let b = parseInt(result[3], 16) / 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  let h
  let s
  const l = (max + min) / 2
  if (max === min) {
    h = s = 0
  } else {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6
        break
      case g:
        h = ((b - r) / d + 2) / 6
        break
      default:
        h = ((r - g) / d + 4) / 6
        break
    }
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(
    l * 100
  )}%`
}

function setCssVar(name, hex) {
  const val = hexToHslComponents(hex)
  if (val) document.documentElement.style.setProperty(name, val)
}

function getRelativeLuminance(hex) {
  const normalized = normalizeHex(hex)
  if (!normalized) return 1
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(normalized)
  if (!result) return 1
  const toLinear = channel => {
    const normalizedChannel = parseInt(channel, 16) / 255
    return normalizedChannel <= 0.03928
      ? normalizedChannel / 12.92
      : Math.pow((normalizedChannel + 0.055) / 1.055, 2.4)
  }
  const r = toLinear(result[1])
  const g = toLinear(result[2])
  const b = toLinear(result[3])
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

function getContrastRatio(hex1, hex2) {
  const lum1 = getRelativeLuminance(hex1)
  const lum2 = getRelativeLuminance(hex2)
  const lighter = Math.max(lum1, lum2)
  const darker = Math.min(lum1, lum2)
  return (lighter + 0.05) / (darker + 0.05)
}

function mixHex(hex, targetHex, amount) {
  const from = normalizeHex(hex)
  const to = normalizeHex(targetHex)
  if (!from || !to) return hex
  const parse = value => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(value)
    if (!result) return null
    return [
      parseInt(result[1], 16),
      parseInt(result[2], 16),
      parseInt(result[3], 16)
    ]
  }
  const fromRgb = parse(from)
  const toRgb = parse(to)
  if (!fromRgb || !toRgb) return hex
  const mix = (a, b) => Math.round(a + (b - a) * amount)
  return `#${[
    mix(fromRgb[0], toRgb[0]),
    mix(fromRgb[1], toRgb[1]),
    mix(fromRgb[2], toRgb[2])
  ]
    .map(channel => channel.toString(16).padStart(2, '0'))
    .join('')}`
}

/**
 * Prefer the client text color when it remains readable on the page background;
 * otherwise fall back to a contrast color (same approach as legacy DescText).
 */
function getReadableTextColor(primaryText, bg, secondaryText) {
  const fallback = secondaryText || getContrastColor(bg)
  if (!primaryText) return fallback
  if (getContrastRatio(primaryText, bg) >= 4.5) return primaryText
  return fallback
}

/** Prefer configured text color when it contrasts with bg; otherwise derive one. */
function readableOn(bg, preferred) {
  if (preferred && getContrastRatio(preferred, bg) >= 4.5) return preferred
  return getContrastColor(bg)
}

/**
 * Syncs the client's dynamic brand colors (from styled-components ThemeProvider)
 * into CSS variables so Tailwind tokens reflect the correct per-client theme.
 *
 * Token mapping:
 *   theme.color.brand        → --primary  (bg-primary, text-primary, border-primary)
 *   theme.color.bg           → --background / --card / --popover
 *   readable text on bg      → --foreground / --card-foreground
 *   theme.color.secondaryText→ --secondary-foreground (fallback)
 *   theme.color.error        → --destructive
 *   theme.color.borderDark   → --border / --muted-foreground (light themes)
 *   theme.color.borderLight  → --input / --muted / --secondary (light themes)
 *   theme.color.waitlistText → --waitlist
 */
export function useClientTheme(theme) {
  useEffect(() => {
    if (!theme?.color?.bg) return

    const {
      brand,
      bg,
      primaryText,
      secondaryText,
      error,
      borderDark,
      borderLight,
      waitlistText
    } = theme.color

    const readableText = getReadableTextColor(primaryText, bg, secondaryText)
    const onBgMuted = readableOn(bg, secondaryText)
    const onBrand = brand ? getContrastColor(brand) : readableText
    const isDarkBg = getRelativeLuminance(bg) < 0.45
    const mutedSurface = isDarkBg ? mixHex(bg, '#ffffff', 0.16) : borderLight
    const mutedText = isDarkBg
      ? mixHex(readableText, bg, 0.35)
      : borderDark || onBgMuted
    const borderColor = isDarkBg
      ? mixHex(bg, '#ffffff', 0.18)
      : borderDark || borderLight || '#eaecf0'
    const inputColor = isDarkBg
      ? mixHex(bg, '#ffffff', 0.12)
      : borderLight || borderColor

    setCssVar('--primary', brand)
    setCssVar('--primary-foreground', onBrand)
    setCssVar('--background', bg)
    setCssVar('--foreground', readableText)
    setCssVar('--card', bg)
    setCssVar('--card-foreground', readableText)
    setCssVar('--popover', bg)
    setCssVar('--popover-foreground', readableText)
    setCssVar('--secondary', mutedSurface)
    setCssVar('--secondary-foreground', onBgMuted)
    setCssVar('--muted', mutedSurface)
    setCssVar('--muted-foreground', mutedText)
    setCssVar('--accent', mutedSurface)
    setCssVar('--accent-foreground', readableText)
    setCssVar('--destructive', error)
    setCssVar('--border', borderColor)
    setCssVar('--input', inputColor)
    setCssVar('--ring', brand)
    if (waitlistText) setCssVar('--waitlist', waitlistText)
    document.documentElement.style.colorScheme = isDarkBg ? 'dark' : 'light'
  }, [theme])
}
